import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { ListItem } from '../../models/ListItem';
import { AcsBillAddress, AcsCommodity, BillAddressEditModel } from '../models/acs.models';
import { ACS_BILLING_ADDRESS_KEY, ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, toTitleCase, resolveLocCode, scopedLocsToListItems, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';
import { AcsDropdownState } from '../utils/acs-dropdown-state';
import { AcsDropdownService } from '../utils/acs-dropdown.service';
import { AcsBulkDeleteState } from '../utils/acs-bulk-delete-state';

@Component({
  selector: 'app-acs-bill-address',
  templateUrl: './acs-bill-address.component.html',
  styleUrls: ['../acs-shared.css'],
  standalone: false
})
export class AcsBillAddressComponent implements OnInit, OnDestroy {

  private _destroy$ = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  billAddresses: AcsBillAddress[] = [];
  locList: ListItem[] = [];
  commodityList: ListItem[] = [];
  locDropdown!: AcsDropdownState;
  commoditySearchText = signal('');
  modalCommoditySearchText = signal('');
  selectedCommodityCode: string = '';
  editingItem: BillAddressEditModel | null = null;
  showDialog: boolean = false;
  bulkDelete = new AcsBulkDeleteState<AcsBillAddress>(a => a.locDefaultId, () => this.billAddresses);

  constructor(
    private _adminConfigService: AdminConfigService,
    public dropdowns: AcsDropdownService
  ) {
    this.locDropdown = new AcsDropdownState('ba-loc', this.dropdowns, () => this.locList, '-- Select Trade Unit --');
  }

  ngOnInit(): void {
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.locList = scopedLocsToListItems(result, true)
    });
    this._adminConfigService.getAllCommodities().pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.commodityList = data.map(c => ({ value: c.commodityCode, text: c.commodityCode + ' - ' + (c.shortDescription || c.description || '') } as ListItem))
    });
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get filteredCommodityList(): ListItem[] {
    const term: string = this.commoditySearchText();
    if (!term) return this.commodityList;
    const lower: string = term.toLowerCase();
    return this.commodityList.filter(c => c.text.toLowerCase().includes(lower) || c.value.toLowerCase().includes(lower));
  }

  get filteredModalCommodityList(): ListItem[] {
    const term: string = this.modalCommoditySearchText();
    if (!term) return this.commodityList;
    const lower: string = term.toLowerCase();
    return this.commodityList.filter(c => c.text.toLowerCase().includes(lower) || c.value.toLowerCase().includes(lower));
  }

  get selectedCommodityDisplay(): string {
    if (!this.selectedCommodityCode) return '-- Select Commodity --';
    if (this.selectedCommodityCode === 'All*') return 'All*';
    const item: ListItem | undefined = this.commodityList.find(c => c.value === this.selectedCommodityCode);
    return item?.text || this.selectedCommodityCode;
  }

  toggleLocDropdown(): void {
    this.locDropdown.toggle();
  }

  toggleCommodityDropdown(): void {
    this.dropdowns.toggle('ba-commodity');
    if (this.dropdowns.isOpen('ba-commodity')) this.commoditySearchText.set('');
  }

  selectLoc(item: ListItem): void {
    this.locDropdown.select(item);
    this.onLocCodeChange();
  }

  toggleModalCommodityDropdown(): void {
    this.dropdowns.toggle('ba-modal-commodity');
    if (this.dropdowns.isOpen('ba-modal-commodity')) this.modalCommoditySearchText.set('');
  }

  selectModalCommodity(item: ListItem): void {
    if (!this.editingItem) return;
    this.editingItem.commodityCode = item.value;
    this.editingItem.commodityDisplay = item.value === 'All*' ? 'All Commodities' : (item.text || item.value);
    this.dropdowns.close();
    this.modalCommoditySearchText.set('');
  }

  selectCommodity(item: ListItem): void {
    this.selectedCommodityCode = item.value;
    this.dropdowns.close();
    this.commoditySearchText.set('');
    this.billAddresses = [];
    if (this.locDropdown.selectedValue || this.selectedCommodityCode) {
      this.loadBillAddresses();
    }
  }

  onLocCodeChange(): void {
    this.billAddresses = [];
    if (this.locDropdown.selectedValue || this.selectedCommodityCode) {
      this.loadBillAddresses();
    }
  }

  loadBillAddresses(): void {
    this.bulkDelete.selectedIds = new Set();
    const locCode: string = this.getEffectiveLocCode();
    this._adminConfigService.getBillAddresses(locCode, this.selectedCommodityCode).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.billAddresses = data,
      error: () => this.toastNotification.showToast('Failed to load billing addresses', ACS_TOAST_ERROR_DURATION, 'error')
    });
  }

  openAdd(): void {
    const locItem: ListItem | undefined = this.locList.find(l => l.value === this.locDropdown.selectedValue);
    const commItem: ListItem | undefined = this.commodityList.find(c => c.value === this.selectedCommodityCode);
    const hasToolbarCommodity: boolean = !!this.selectedCommodityCode && this.selectedCommodityCode !== 'All*';
    this.editingItem = {
      locDefaultId: 0,
      locCode: this.getEffectiveLocCode(),
      locCodeDisplay: locItem?.text || this.getEffectiveLocCode(),
      commodityCode: hasToolbarCommodity ? this.selectedCommodityCode : '',
      commodityDisplay: hasToolbarCommodity ? (commItem?.text || this.selectedCommodityCode) : '-- Select Commodity --',
      key: ACS_BILLING_ADDRESS_KEY,
      value: ''
    };
    this.dropdowns.close();
    this.modalCommoditySearchText.set('');
    this.showDialog = true;
  }

  openEdit(item: AcsBillAddress): void {
    const commItem: ListItem | undefined = this.commodityList.find(c => c.value === item.commodityCode);
    this.editingItem = {
      ...item,
      key: ACS_BILLING_ADDRESS_KEY,
      locCodeDisplay: item.locCode,
      commodityDisplay: item.commodityCode === 'All*' ? 'All Commodities' : (commItem?.text || item.commodityCode)
    };
    this.showDialog = true;
  }

  save(): void {
    const item = this.editingItem;
    if (!item) return;

    const formattedValue: string = item.value
      ? item.value
          .split('\n')
          .map((line: string) => toTitleCase(line.trim()))
          .filter((line: string) => line.length > 0)
          .join('\n')
      : '';

    const dto = {
      locDefaultId: item.locDefaultId || 0,
      locCode: item.locCode,
      commodityCode: item.commodityCode || 'All*',
      key: ACS_BILLING_ADDRESS_KEY,
      value: formattedValue
    };

    if (dto.locDefaultId === 0) {
      const duplicate = this.billAddresses.find(a =>
        a.locCode === dto.locCode &&
        a.commodityCode === dto.commodityCode
      );
      if (duplicate) {
        this.toastNotification.showToast('A billing address already exists for this Trade Unit and Commodity', ACS_TOAST_ERROR_DURATION, 'error');
        return;
      }
    }

    this._adminConfigService.saveTradeUnitDefault(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => {
        if (result) {
          this.toastNotification.showToast('Billing address saved', ACS_TOAST_SUCCESS_DURATION, 'success');
          this.closeDialog();
          this.loadBillAddresses();
        }
      },
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save billing address');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  closeDialog(): void {
    this.editingItem = null;
    this.showDialog = false;
    this.dropdowns.close();
    this.modalCommoditySearchText.set('');
  }

  // --- Bulk Delete ---

  confirmBulkDelete(): void {
    this.bulkDelete.isDeleting = true;
    const ids: number[] = [...this.bulkDelete.selectedIds];
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteTradeUnitDefault(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'billing address');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          this.bulkDelete.reset();
          this.loadBillAddresses();
        }
      });
  }

  private getEffectiveLocCode(): string {
    return resolveLocCode(this.locDropdown.selectedValue, this.locList);
  }

}
